import Papa from "papaparse"
import { camelize, MaybeNumber } from "../displayFormatters"
import isEqual from 'lodash/isEqual'
import groupBy from 'lodash/groupBy'

export const possibleAttributes = [
  { label: 'Cost', value: 'cost' },
  { label: 'Class', value: 'class' },
  { label: 'Custom Margin', value: 'customMargin' },
  { label: 'Custom Price', value: 'customPrice' },
  { label: 'Price', value: 'price' },
  { label: 'Tags', value: 'tags' },
  { label: 'Categories', value: 'categories' },
  { label: 'Taxable', value: 'taxable' }
]

export type ParsedCsvRow = {
  name: string,
  sku: string,
  cost: string,
  customPrice: string,
  customMargin: string,
  class: string,
  tags: string,
  categories: string,
  taxable?: string
}

export type ParsedCsv = ParsedCsvRow[]

export type ConvertedCsvData = {
  name: string,
  sku: string,
  cost: MaybeNumber,
  customPrice: MaybeNumber,
  customMargin: MaybeNumber,
  class: number,
  tags: string | null,
  categories: string | null,
  taxable: boolean
}

export const parseFile = (file: string): Promise<ParsedCsv> => {
  return new Promise(resolve => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: function(h) {
        const word = camelize(h)
        if (word === 'sKU') {
          return 'sku'
        }
        return word
      },
      complete: results => {
        resolve(results.data as ParsedCsv)
      }
    })
  })
}

export const dollarsInCents = (value: string | undefined | null): MaybeNumber => {
  if(value === undefined || value === null){
    return null
  }
  // replace non numeric characters
  const numericCharactersValue = value.toString().replace(/[^-\d.]/g, "")
  // parse float
  const floatVal = parseFloat(numericCharactersValue)
  // not a number check
  if (floatVal === null || isNaN(floatVal))
    return null;
  // convert to cents
  return Math.round(floatVal * 100);
}

export const parseClass = (classString: string): number => {
  const validClasses = [0, 1, 2, 3, 4, 99]
  const parsed = parseInt(classString)
  return validClasses.includes(parsed) ? parsed : 2
}

const parseBoolean = (input?: string): boolean => {
  return (input !== undefined && (input.toLowerCase() === 'true' || input === '1'))
}

export const getConvertedCsvData = (csvData: ParsedCsv, timestamp: Date): ConvertedCsvData[] => {
  const zeroPad = (input: number) => input < 10 ? `0${input}` : `${input}`
  const generatedSkuPrefix = `IM-${timestamp.getUTCFullYear()}${zeroPad(timestamp.getUTCMonth() + 1)}${zeroPad(timestamp.getUTCDate())}` +
    `${zeroPad(timestamp.getUTCHours())}${zeroPad(timestamp.getUTCMinutes())}${zeroPad(timestamp.getUTCSeconds())}-`
  return csvData.map((item, index) => ({
    ...item,
    name: item.name ? item.name.trim() : '',
    sku: item.sku ? item.sku.trim() : `${generatedSkuPrefix}${index + 1}`,
    cost: dollarsInCents(item.cost) || 0,
    customPrice: dollarsInCents(item.customPrice),
    customMargin: convertCustomMargin(item.customMargin),
    class: parseClass(item.class),
    tags: item.tags !== '' ? item.tags : null,
    categories: item.categories !== '' ? item.categories : null,
    taxable: parseBoolean(item.taxable)
  }))
}

const convertCustomMargin = (value: string): number | null => {
  const parsed = parseFloat(value)
  if (parsed > 99.99) {
    return 99.99
  }
  if (parsed >= 0) {
    return parsed
  }
  return null
}

export const filterOutEmptyRecords = (csvData: ParsedCsv): ParsedCsv => {
  return csvData.filter(x => Object.values(x).some(y => !!y))
}

export const getUniqueRecords = (csvData: ParsedCsv): ParsedCsv => {
  const result: ParsedCsv = []
  csvData.forEach(row => {
    if (!result.some(x => isEqual(row, x))) {
      result.push(row)
    }
  })

  return result
}

type Duplicate = { sku?: string, name?: string }

export const checkForDuplicates = (rowKey: 'name' | 'sku' | 'both', csvData: ParsedCsv): Duplicate[] => {
  const getKey = (row: ParsedCsvRow): string => {
    if (rowKey === 'both') {
      return `${row.name}:${row.sku}`
    }

    return row[rowKey]
  }

  const getDuplicateResult = (row: ParsedCsvRow): Duplicate => {
    if (rowKey === 'name') {
      return { name: row.name }
    }
    if (rowKey === 'sku') {
      return { sku: row.sku }
    }
    return { name: row.name, sku: row.sku }
  }

  return Object.values(groupBy(csvData, getKey))
    .filter(v => v.length > 1)
    .map(v => getDuplicateResult(v[0]))
}
